import { useNavigate, useParams } from 'react-router-dom';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import './dataPipeline.scss';
import '../LandingPage/LandingPage.scss';
import { Box, Dialog, IconButton, Typography } from '@mui/material';
import moment from 'moment';
import { ReactComponent as EditIcon } from '../../assets/edit-img.svg';
import { ReactComponent as DeleteIcon } from '../../assets/delete-img.svg';
import { getExecutiveProjData, getE2EDashboardData } from 'service/service';
import Loader from 'components/Loader/loader';
import ProgramDataCard from './ExeProjectDataCard';
import PageWrapper from 'components/Common/PageWraper';
import CustomLinearProgress from 'components/Inputs/DataPipeline/CustomLinearProgress';
import { DataPipelineModal } from './modal-component';
import { clearSessionData } from './DataPipelineHelper';
import { FilterIcon } from 'components/CommonIcons/Icons';
import FilterDrawer from '../FilterDrawer/FilterDrawer';
import { FilterState } from '../FilterDrawer/types';
import { DEFAULT_FILTERS } from '../FilterDrawer/constants';
import { useE2EDashboard, buildBaseDashboardFilterFields, PROGRAM_DASHBOARD_EXTRA_FIELD_KEYS, buildProgramDashboardFilterFields } from './E2EDashboardHooks/useE2EDashboard';
import { useHeader } from 'Context/contexts/HeaderTabRightContentContext';
import CustomAccordion from 'components/Accordion/CustomAccordion';
import { useTheme } from '@mui/material/styles';

const VALID_DASHBOARD_TYPES = ['sector', 'program'] as const;
type DashboardType = typeof VALID_DASHBOARD_TYPES[number];

const DASHBOARD_TYPE_LABELS: Record<DashboardType, string> = {
    sector: 'Sector Dashboards',
    program: 'Program Dashboards',
};

const isMiscellaneousSectorSelectionMissing = (filters: FilterState): boolean => {
    const hasMiscSelected = (filters.sector || []).some((s: any) => {
        const value = s?.value ?? s;
        return String(value).trim().toLowerCase() === 'miscellaneous';
    });
    const selectedMiscSectors = filters.miscellaneousSectors || [];
    return hasMiscSelected && selectedMiscSectors.length === 0;
};

const isMiscSelected = (items: any[] = []) =>
    Array.isArray(items) && items.some((s: any) => {
        const value = s?.value ?? s;
        return String(value).trim().toLowerCase() === 'miscellaneous';
    });
 
type E2EFilterDrawerProps = Omit<
    React.ComponentProps<typeof FilterDrawer>,
    'filterFields' | 'currentFilters' | 'onFilterChange' | 'applyDisabled'
> & {
    currentFilters: FilterState;
    filterOptions: ReturnType<typeof useE2EDashboard>['filterOptions'];
    loadingFilterFields: Record<string, boolean>;
    dashboardType: DashboardType;
};
 
const E2EFilterDrawer = ({
    currentFilters: propFilters,
    filterOptions,
    loadingFilterFields,
    dashboardType,
    ...rest
}: E2EFilterDrawerProps) => {
    const [draftFilters, setDraftFilters] = useState<FilterState>(propFilters);
    const [hasInitialized, setHasInitialized] = useState(false);
    const lastSyncedFiltersJsonRef = useRef<string>(JSON.stringify(propFilters));
 
    useEffect(() => {
        const propFiltersJson = JSON.stringify(propFilters);
        if (propFiltersJson !== lastSyncedFiltersJsonRef.current) {
            lastSyncedFiltersJsonRef.current = propFiltersJson;
            setDraftFilters(propFilters);
        }
    }, [propFilters]);
 
    useEffect(() => {
        if (rest.open && !hasInitialized) {
            setHasInitialized(true);
            setDraftFilters(propFilters);
            lastSyncedFiltersJsonRef.current = JSON.stringify(propFilters);
        }
        if (!rest.open) {
            setHasInitialized(false);
        }
    }, [rest.open, propFilters]);

    useEffect(() => {
        if (rest.isInitialLoading) return;

        const invalidFilters = (Object.keys(filterOptions) as (keyof FilterState)[]).reduce(
            (invalid, field) => {
                const selected = draftFilters[field] as any[];
                const options = filterOptions[field];
                if (!Array.isArray(selected) || !selected.length || !Array.isArray(options)) return invalid;

                const availableValues = new Set(
                    options.map((option: any) => String(option?.value ?? option).trim().toLowerCase()),
                );
                const validValues = selected.filter((option: any) =>
                    availableValues.has(String(option?.value ?? option).trim().toLowerCase()),
                );

                if (validValues.length !== selected.length) {
                    invalid[field] = validValues;
                }
                return invalid;
            },
            {} as Record<string, any>,
        );

        if (Object.keys(invalidFilters).length) {
            setDraftFilters((prev) => {
                const next = { ...prev, ...invalidFilters };
                if (invalidFilters.program) {
                    PROGRAM_DASHBOARD_EXTRA_FIELD_KEYS.forEach((key) => {
                        if (key !== 'program') delete (next as any)[key];
                    });
                }
                return next;
            });
        }
    }, [filterOptions, loadingFilterFields, draftFilters, rest.isInitialLoading]);
 
    const processedFields = useMemo(
        () => dashboardType === 'program'
            ? buildProgramDashboardFilterFields(filterOptions, draftFilters, loadingFilterFields)
            : buildBaseDashboardFilterFields(filterOptions, draftFilters, loadingFilterFields),
        [dashboardType, filterOptions, draftFilters, loadingFilterFields]
    );
    
    const isApplyDisabled = useMemo(
        () => isMiscellaneousSectorSelectionMissing(draftFilters),
        [draftFilters]
    );
 
    const handleLocalFilterChange = useCallback((field: keyof FilterState, newValue: any) => {
        setDraftFilters((prev) => {
            const next: FilterState = { ...prev, [field]: newValue };

            if (field === 'sector') {
                const hasMisc = isMiscSelected(newValue);
                if (hasMisc) {
                    next.miscellaneousSectors = next.miscellaneousSectors?.length
                        ? next.miscellaneousSectors
                        : [{ label: 'All', value: 'all' }];
                } else {
                    next.miscellaneousSectors = [];
                }

            }

            if (field === 'program' && dashboardType === 'program') {
                PROGRAM_DASHBOARD_EXTRA_FIELD_KEYS.forEach((key) => {
                    if (key !== 'program') delete (next as any)[key];
                });
            }

            return next;
        });
    }, [dashboardType]);

    const handleLocalFilterReset = useCallback(() => {
        setDraftFilters({
            ...DEFAULT_FILTERS,
            miscellaneousSectors: [],
        });    
    }, []);
 
    return (
        <FilterDrawer
            {...rest}
            currentFilters={draftFilters}
            filterFields={processedFields}
            onFilterChange={handleLocalFilterChange}
            onReset={handleLocalFilterReset}
            applyDisabled={isApplyDisabled}
            isProgramDashboard={dashboardType === 'program'}
        />
    );
};

export default function ExecutiveOps() {
    const { setRightContextContent } = useHeader();
    const { dashboardType: dashboardTypeParam } = useParams<{ dashboardType?: string }>();
    const navigate = useNavigate();
    const dashboardType: DashboardType = VALID_DASHBOARD_TYPES.includes(dashboardTypeParam as DashboardType)
        ? (dashboardTypeParam as DashboardType)
        : 'sector';

    const filterConfig = useMemo(() => ({
        dashboardId: `${dashboardType}-dashboards`,
        dashboardName: DASHBOARD_TYPE_LABELS[dashboardType],
        dashboardType,
    }), [dashboardType]);

    const startDate = moment().tz("America/Chicago").startOf('day').format('YYYY-MM-DD');
    const [dataCardLoader, setDataCardLoader] = useState<any>(false);
    const [timeLineInput, setTimeLineInput] = useState<any>([])
    const [timelineLoader, setTimelineLoader] = useState<any>(true)
    const [programData, setProgramData] = useState<any[]>([])
    const [initialData, setInitialData] = useState<any[]>([])
    const [expandedPanel, setExpandedPanel] = useState(null);
    const [openModal, setOpenModal] = useState<any>({ isOpen: false });
    const [hasAccess, setHasAccess] = useState<boolean>(true);
    const theme = useTheme();
    const colors = theme.palette.dataPipeline.accordionWidget;
    const accordionHeaderColors = colors.accordionHeader;

    const {
        isFilterDrawerOpen,
        openFilterDrawer,
        closeFilterDrawer,
        appliedFilters,
        filterOptions,
        loadingFilterFields,
        isInitialFilterLoading,
        loadAllDropDowns,
        handleApplyFilters,
        handleSaveAsDefault,
        handleResetToDefault,
        hasSavedFilters,
        savedDefaultFilters,
    } = useE2EDashboard(filterConfig);

    useEffect(() => {
        const date = new Date();
        date.setMinutes(date.getMinutes() - 10);
        if (!openModal.isOpen) {
            getExecutiveProgramData()
        }

        const activeId = document.getElementById('root');
        if (window.location.pathname.includes('/data-pipeline/executiveOps')) {
            if (activeId) {
                document.head.classList.add('theme-bg-blue');
                document.body.classList.add('theme-bg-blue');
            }
        }
    }, [openModal, appliedFilters]);

    const handleDateChange = (dateValue: any) => {

        onDateFilterChange(dateValue)
    }

    const dataReconDashboardCards = useMemo(() => new Set([
        'EMEA PepSell PepIris E2E Dashboard',
        'EMEA PepSell PepIris Master Data E2E Dashboard',
    ]), []);

    const onDateFilterChange = async (dateValue: any) => {
        try {
            setDataCardLoader(true)
            let response = await getExecutiveProjData(dateValue.scheduleDate, dateValue.programId);
            setDataCardLoader(false)
            if (response.success && response.data.length) {
                const responseProgramId = response.data[0].programId;

                // Find the matching program in programData
                const index = programData.findIndex((element: any) => element.programId === responseProgramId);

                if (index !== -1) {
                    const updatedProgramData = [...programData]; // avoid direct mutation
                    const existingProjectDetails = updatedProgramData[index].projectDetails;

                    const responseProjectDetails = response.data;


                    // Update each project in projectDetails
                    const mergedProjectDetails = existingProjectDetails.map((project: any) => {
                        const match = responseProjectDetails.find((p: any) => project.hopType == 'program' ? p.projectName === project.projectName : (p.sstnProjectId == project.projectId) || (p.projectName === project.projectName));

                        return {
                            ...project,
                            ...(match)
                        };
                    });

                    updatedProgramData[index] = {
                        ...updatedProgramData[index],
                        projectDetails: mergedProjectDetails
                    };


                    setProgramData(updatedProgramData);
                }
            }

        } catch (error) {
            console.error(error);
            throw error
        }
    }

    const getExecutiveProgramData = async () => {
        try {
            setTimelineLoader(true)
            setHasAccess(true);

            // Build globalFilters from appliedFilters
            const globalFilters: Record<string, any> = {};
            (Object.keys(appliedFilters) as (keyof FilterState)[]).forEach((key) => {
                if (key === 'additionalFilters') return;
                const value = appliedFilters[key];
                if (Array.isArray(value) && value.length) {
                    const values = (value as any[]).map((v) => v?.value || v);
                    const hasAll = values.some((v) => String(v).toLowerCase() === 'all');
                    globalFilters[key] = hasAll ? ['all'] : values;
                }
            });

            const params = {
                globalFilters,
                scheduleDate: startDate,
                dashboardType,
            };

            let response = await getE2EDashboardData(params);
            setTimelineLoader(false)

            if (response?.status === 403) {
                setHasAccess(false);
                return;
            }
            if (response.success) {
                const data = response.data.filter(Boolean)

                setProgramData(data)
                setInitialData(data)
            }



        } catch (error) {
            console.error(error);
            throw error
        }
    }

    const handleEditClose = useCallback((data: any) => {
        setOpenModal({
            isOpen: false
        });
    }, []);

    const handleAction = (action: any, data: any, e: any) => {
        e.preventDefault();
        e.stopPropagation();
        switch (action) {
            case 'edit':
                setOpenModal({
                    ...openModal,
                    isOpen: true,
                    type: 'Dashboard',
                    action: 'EDIT',
                    data: data
                });
                clearSessionData();
                navigate(`/data-pipeline/executiveTemplate?programId=${data.programId}&programName=${data.programName}&sector=${data.sector}&isEdited=true`);
                break;
            case 'delete':
                setOpenModal({
                    ...openModal,
                    isOpen: true,
                    type: 'Dashboard',
                    action: 'DELETE',
                    data: data
                });
                break;
            default:
                break;
        }
    };

    const handleAccordianChange = (panel: any, index: number, programId: any) => (event: any, isExpanded: boolean) => {

        let reqData: any = initialData.filter((id: any) => id.programId == programId)
        setTimeLineInput(reqData[0]?.projectDetails?.filter((dat: any) => dat.isAvailable))
        setExpandedPanel(isExpanded ? panel : null);
    };

    useEffect(() => {
        setRightContextContent(
            <Box display="flex" alignItems="center" gap={1}>
            <IconButton
                onClick={openFilterDrawer}
                sx={{ position: 'relative', p: '0px !important' }}
            >
                {<FilterIcon />}
            </IconButton>
            </Box>
        );

        return () => {
            setRightContextContent(null);
        };
        }, [
        setRightContextContent
    ]);

    return (
        hasAccess && (
            <PageWrapper>
                <Box className='exe-timeline' bgcolor="backgroundLight.primary">
                    {timelineLoader ? ( <Loader />
                    ) : (
                        programData.map((program: any, index: number) => {
                            return (
                                <Box
                                    key={program.programId ?? index}
                                    mt={4}
                                    mb={3}
                                    sx={{
                                        '& > .MuiAccordion-root': {
                                            border: expandedPanel == `panel${index + 1}`
                                                ? `1px solid ${colors.border.expandedAccordion} !important`
                                                : 'none',
                                            borderRadius: '4px',
                                            overflow: 'hidden',
                                        },
                                        '& > .MuiAccordion-root.Mui-expanded': {
                                            border: `1px solid ${colors.border.expandedAccordion} !important`,
                                        },
                                    }}
                                >
                                    <CustomAccordion
                                        id={`panel${index + 1}`}
                                        title={
                                            program.programName ||
                                            (program.programId == 1 ? 'APAC Thailand' : 'APAC Vietnam')
                                        }
                                        headerAction={
                                            dataReconDashboardCards.has(String(program.programName || '').trim()) ? (
                                                <Box
                                                    component="span"
                                                    onClick={(e) => {
                                                        e.preventDefault();
                                                        e.stopPropagation();
                                                        navigate(
                                                            `/data-pipeline/executiveOps/pepsellPepIrisRecon?fromType=${dashboardType}&fromProgram=${encodeURIComponent(program.programName)}`
                                                        );
                                                    }}
                                                    sx={{
                                                        color: '#7dd3fc',
                                                        fontSize: '14px',
                                                        fontWeight: 600,
                                                        textDecoration: 'underline',
                                                        cursor: 'pointer',
                                                        whiteSpace: 'nowrap',
                                                    }}
                                                >
                                                    Data Recon Dashboard
                                                </Box>
                                            ) : null
                                        }
                                        expanded={expandedPanel == `panel${index + 1}`}
                                        onChange={
                                            program.isProgramActive
                                                ? handleAccordianChange(
                                                    `panel${index + 1}`,
                                                    index,
                                                    program.programId
                                                )
                                                : undefined
                                        }
                                        isActive={Boolean(program.isProgramActive)}
                                        statusLabel={program.slaStatus}
                                        contactInfo={program.contactInfo}
                                        statusImageUrl={program.exeIconPath}
                                        legendData={program.legendData}
                                        moreActionItems={[
                                            {
                                                key: 'edit',
                                                label: 'Edit',
                                                icon: <EditIcon />,
                                                onMenuClick: (e: any) =>
                                                    handleAction('edit', program, e)
                                            },
                                            {
                                                key: 'delete',
                                                label: 'Delete',
                                                icon: <DeleteIcon />,
                                                onMenuClick: (e: any) =>
                                                    handleAction('delete', program, e)
                                            }
                                        ]}
                                        etaLabel={
                                            <>
                                                <Typography
                                                    variant="body2"
                                                    sx={{
                                                        fontSize: '13px',
                                                        color: accordionHeaderColors.tooltipTitle,
                                                        fontWeight: 500,
                                                        fontStyle: 'italic',
                                                    }}
                                                >
                                                    Est. Arrival
                                                </Typography>

                                                <Typography
                                                    variant="body2"
                                                    sx={{
                                                        fontSize: '12px'
                                                    }}
                                                >
                                                    {program.exeProgramEta
                                                        ? moment(program.exeProgramEta)
                                                            .utc()
                                                            .format('MMM DD, YYYY')
                                                        : '--'}
                                                </Typography>
                                            </>
                                        }
                                        renderExpandedContent={
                                            timeLineInput?.length ? (
                                                <CustomLinearProgress
                                                    timeline={timeLineInput}
                                                    isPBNA={String(program.sector).trim().toUpperCase() === 'PBNA'}
                                                />
                                            ) : (
                                                <Box
                                                    sx={{ width: '100%' }}
                                                    display="flex"
                                                    alignItems="center"
                                                    justifyContent="center"
                                                >
                                                    <Typography
                                                        sx={{
                                                            textAlign: 'center',
                                                            border: `1px solid ${accordionHeaderColors.comingSoonBorder}`,
                                                            borderRadius: '20px',
                                                            padding: '2px 8px',
                                                            display: 'inline-flex',
                                                            alignItems: 'center',
                                                            fontSize: '12px',
                                                            backgroundColor: accordionHeaderColors.comingSoonBackground,
                                                            color: colors.text.inverse,
                                                            fontWeight: 600,
                                                            lineHeight: 1.2,
                                                        }}
                                                    >
                                                        COMING SOON
                                                    </Typography>
                                                </Box>
                                            )
                                        }
                                        renderDetails={
                                            program.projectDetails?.length > 0 ? (
                                                <Box>
                                                    <ProgramDataCard
                                                        programId={program.programId}
                                                        projectDetails={program.projectDetails}
                                                        dataCardLoader={dataCardLoader}
                                                        handleDateChange={handleDateChange}
                                                        sector={program.sectors}
                                                    />
                                                </Box>
                                            ) : null
                                        }
                                        colors={colors}
                                    />
                                </Box>
                            );
                        })
                    )}

                    <Dialog open={openModal.isOpen} onClose={handleEditClose} fullWidth={true} maxWidth="md">
                        <DataPipelineModal handleEditClose={handleEditClose} programData={openModal} />
                    </Dialog>
                </Box>

                <E2EFilterDrawer
                    open={isFilterDrawerOpen}
                    onClose={closeFilterDrawer}
                    onApply={handleApplyFilters}
                    onSaveAsDefault={handleSaveAsDefault}
                    onResetToDefault={handleResetToDefault}
                    hasSavedFilters={hasSavedFilters}
                    savedDefaultFilters={savedDefaultFilters}
                    currentFilters={{ ...appliedFilters, miscellaneousSectors: appliedFilters.miscellaneousSectors || [] }}
                    filterOptions={filterOptions}
                    loadingFilterFields={loadingFilterFields}
                    dashboardType={dashboardType}
                    onDropdownOpen={(draftFilters: FilterState, field: keyof FilterState) => loadAllDropDowns(false, draftFilters, field)}
                    hideAdditionalFilters
                    hideMoreFilters
                    isInitialLoading={isInitialFilterLoading}
                />
            </PageWrapper>
        )
    );
}